import { useEffect, useLayoutEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import fm from 'format-message';
import PropTypes from 'prop-types';
import SpinnerIcon from '@activebrands/core-web/components/icons/SpinnerIcon';
import ErrorNotification from '@activebrands/core-web/components/notifications/ErrorNotification';
import media from '@activebrands/core-web/config/media';
import CheckoutContainer from '@activebrands/core-web/containers/CheckoutContainer';
import useNotifications from '@activebrands/core-web/hooks/useNotifications';
import Events from '@activebrands/core-web/libs/Events';
import { BasketEvents } from '@activebrands/core-web/libs/Events/types';
import overlay from '@activebrands/core-web/libs/overlay';
import { styled } from '@activebrands/core-web/libs/styletron';
import extractQueryParams from '@grebban/utils/string/extractQueryParams';
import { useLocation } from '@reach/router';
import CheckoutView from 'templates/CheckoutPage/CheckoutView';
import EmptyCheckoutView from 'templates/CheckoutPage/EmptyCheckoutView';

const Wrapper = styled('div', ({ $isEmptyCheckout }) => ({
    [media.min['desktop.sm']]: {
        maxWidth: $isEmptyCheckout ? '100%' : 'var(--checkout-corridor-max-width)',
        margin: '0 auto',
        borderRight: '1px solid var(--color-border-checkout-subtle)',
        borderLeft: '1px solid var(--color-border-checkout-subtle)',
    },
}));

const LoadingWrapper = styled('div', {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
});

const CheckoutPage = ({ pageContext = undefined, previewDataOverride = undefined }) => {
    const { content } = previewDataOverride || pageContext?.page.data || {};
    const [loading, setLoading] = useState(false);

    const errorText = content.errorParagraph?.[0]?.richText;

    const basket = useSelector(state => state.basket);
    const basketItems = useSelector(state => state.basket.items);
    const isEmptyCheckout = basketItems < 1;

    // previewStateOverride is used by storyblok preview
    const showCheckout = (basket?.id && basketItems?.length > 0) || content.previewStateOverride;

    const location = useLocation();
    const notifications = useNotifications();
    const { error: checkoutError } = extractQueryParams(location.search);

    // Display spinner until basket has loaded
    useLayoutEffect(() => {
        const basketHasLoaded = !!basket?.id;

        // Display an error notification if the basket takes too long to load
        const basketTimeout = setTimeout(() => {
            setLoading(false);
            notifications.push(ErrorNotification, {
                message: `${fm('Something went wrong...')} ${fm(
                    'We were not able to fetch your basket. Try to reload the page.'
                )}`,
            });
        }, 6000);

        if (basketHasLoaded) {
            setLoading(false);
            // Clear error timeout if basket has loaded
            clearTimeout(basketTimeout);
        } else {
            setLoading(true);
        }

        return () => {
            clearTimeout(basketTimeout);
        };
    }, [basket?.id]);

    // Error on load
    useEffect(() => {
        let checkoutErrorTimeout;
        if (checkoutError) {
            // Run after 1 millisecond because we need to wait for the OVERLAY.STATE_CHANGE event to finish
            checkoutErrorTimeout = setTimeout(() => {
                overlay.open('error-overlay', {
                    label: content.errorLabel,
                    message: errorText,
                });
            }, 1);
        }

        return () => {
            clearTimeout(checkoutErrorTimeout);
        };
    }, [checkoutError]);

    // Error on event
    useEffect(() => {
        Events.subscribe(BasketEvents.ERROR, message => {
            notifications.push(ErrorNotification, { message });
        });
        return () => {
            Events.unsubscribe(BasketEvents.ERROR);
        };
    }, []);

    return (
        <>
            <Helmet>
                <script src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit" />
            </Helmet>
            <Wrapper $isEmptyCheckout={isEmptyCheckout}>
                {loading ? (
                    <LoadingWrapper>
                        <SpinnerIcon />
                    </LoadingWrapper>
                ) : showCheckout ? (
                    <CheckoutContainer defaultPaymentMethod={['adyen-drop-in', 'adyen-drop-in-us']}>
                        <CheckoutView content={content} />
                    </CheckoutContainer>
                ) : (
                    <EmptyCheckoutView content={content} />
                )}
            </Wrapper>
        </>
    );
};

CheckoutPage.propTypes = {
    pageContext: PropTypes.object,
    previewDataOverride: PropTypes.object,
};

export default CheckoutPage;
